import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppAuthService } from '@src/app/core/services/app-auth.service';
import { SIGN_IN_STEP } from '@src/app/mepco-public/models/mfa-challenges.model';
import { map } from 'rxjs/operators';

import { MOBILE_BREAKPOINT } from '../mfa.constants';

@Component({
  selector: 'app-mfa-setup',
  templateUrl: './mfa-setup.component.html',
  styleUrls: ['./mfa-setup.component.scss'],
  standalone: false,
})
export class MfaSetupComponent {
  private _authService = inject(AppAuthService);

  selectedMethod = input.required<SIGN_IN_STEP>();

  appType = SIGN_IN_STEP.SOFTWARE_TOKEN_MFA;
  smsType = SIGN_IN_STEP.SMS_MFA;
  emailType = SIGN_IN_STEP.EMAIL_MFA;

  setupError: string | null = null;
  isInitializing = signal<boolean>(false);
  isLoading: boolean = false;

  totpSecret = signal<string>('');
  otpAuthUrl = signal<string>('');

  txtDescription = signal<string>('');

  private _breakpointObserver = inject(BreakpointObserver);

  private _isMobile = toSignal(this._breakpointObserver.observe(MOBILE_BREAKPOINT).pipe(map(state => state.matches)), {
    initialValue: this._breakpointObserver.isMatched(MOBILE_BREAKPOINT),
  });

  qrSize = computed(() => (this._isMobile() ? 150 : 200));

  private readonly _issuer = 'Mepco';

  constructor() {
    effect(async () => {
      switch (this.selectedMethod()) {
        case SIGN_IN_STEP.SOFTWARE_TOKEN_MFA:
          await this.initializeTotpSetup();
          break;
        case SIGN_IN_STEP.SMS_MFA:
          {
            const phoneNumber = this._authService.userAttributes().phone_number;
            this.txtDescription.set(`A new SMS code will be sent to the phone  ${this.maskPhone(phoneNumber)}`);
          }
          break;
        case SIGN_IN_STEP.EMAIL_MFA:
          {
            const email = this._authService.userAttributes().email;
            this.txtDescription.set(`A new EMAIl code will be sent to the email  ${this.maskEmail(email)}`);
          }
          break;
        default:
          this.setupError = 'Unsupported MFA method selected.';
      }
    });
  }

  private async initializeTotpSetup(): Promise<void> {
    this.isInitializing.set(true);
    try {
      const totpSetupDetails = await this._authService.getMfaSetupSecret(this._issuer);

      this.otpAuthUrl.set(totpSetupDetails.qrCodeUri.href);
      this.totpSecret.set(totpSetupDetails.secretCode);

      this.setupError = null;
    } catch (error: any) {
      // If the account already has TOTP/MFA configured, treat as completed.
      if (error?.message?.includes('already configured') || error?.code === 'NotAuthorizedException') {
        console.error(error?.message);
        return;
      }
      this.setupError = error?.message ?? 'Unable to initialize MFA setup.';
    } finally {
      this.isInitializing.set(false);
    }
  }

  maskPhone(value: string | number): string {
    if (!value) return '';

    const phoneStr = value.toString();
    const lastFour = phoneStr.slice(-4);

    return lastFour.padStart(phoneStr.length, '*');
  }

  maskEmail(value: string): string {
    if (!value) return '';

    const idx = value.indexOf('@');
    if (idx <= 1) return value;

    const namePart = value.slice(0, idx);
    const domainPart = value.slice(idx);

    const maskedName = namePart[0] + '*'.repeat(namePart.length - 1);

    return maskedName + domainPart;
  }
}
